import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import IndexNav from "../../IndexNav";
import { openAlert } from "../../../Redux/Slices/alertSlice";
import { getReviews, submitReview } from "../../../Services/engagementService";
import {
  Content,
  DateText,
  Empty,
  Form,
  FormCard,
  FormText,
  FormTitle,
  Grid,
  Header,
  HeaderText,
  HeaderTitle,
  Input,
  Page,
  RatingRow,
  RatingText,
  ReviewCard,
  ReviewContent,
  ReviewHead,
  ReviewList,
  ReviewPanel,
  ReviewTitle,
  Reviewer,
  Row,
  StarButton,
  Stars,
  SubmitButton,
  TextArea,
} from "./Styled";

const getStarString = (rating) => "★".repeat(rating) + "☆".repeat(5 - rating);

const Reviews = () => {
  const dispatch = useDispatch();
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    content: "",
    rating: 5,
  });

  const loadReviews = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const data = await getReviews();
      setReviews(data);
    } catch (error) {
      dispatch(
        openAlert({
          message: error?.response?.data?.errMessage ? error.response.data.errMessage : error.message,
          severity: "error",
        })
      );
    }
    setLoadingReviews(false);
  }, [dispatch]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const onChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitReview(formData);
      dispatch(
        openAlert({
          message: "Review submitted successfully.",
          severity: "success",
        })
      );
      setFormData({
        name: "",
        email: "",
        content: "",
        rating: 5,
      });
      await loadReviews();
    } catch (error) {
      dispatch(
        openAlert({
          message: error?.response?.data?.errMessage ? error.response.data.errMessage : error.message,
          severity: "error",
        })
      );
    }
    setSubmitting(false);
  };

  return (
    <>
      <IndexNav />
      <Page>
        <Content>
          <Header>
            <HeaderTitle>Community reviews</HeaderTitle>
            <HeaderText>
              Read what teams say about PepHub Board, and submit your own public review below.
            </HeaderText>
          </Header>
          <Grid>
            <FormCard>
              <Form onSubmit={onSubmit}>
                <div>
                  <FormTitle>Submit your review</FormTitle>
                  <FormText>Reviews are stored in the database and displayed publicly on this page.</FormText>
                </div>
                <Row>
                  <Input
                    required
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => onChange("name", e.target.value)}
                  />
                  <Input
                    required
                    type="email"
                    placeholder="Your email"
                    value={formData.email}
                    onChange={(e) => onChange("email", e.target.value)}
                  />
                </Row>
                <RatingRow>
                  {[1, 2, 3, 4, 5].map((item) => (
                    <StarButton key={item} type="button" active={item <= formData.rating} onClick={() => onChange("rating", item)}>
                      ★
                    </StarButton>
                  ))}
                  <RatingText>{formData.rating} / 5</RatingText>
                </RatingRow>
                <TextArea
                  required
                  placeholder="Describe your experience..."
                  value={formData.content}
                  onChange={(e) => onChange("content", e.target.value)}
                />
                <SubmitButton type="submit" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit review"}
                </SubmitButton>
              </Form>
            </FormCard>
            <ReviewPanel>
              <ReviewTitle>Latest reviews</ReviewTitle>
              {loadingReviews && <Empty>Loading reviews...</Empty>}
              {!loadingReviews && reviews.length === 0 && (
                <Empty>No reviews yet. Be the first to post one.</Empty>
              )}
              {!loadingReviews && reviews.length > 0 && (
                <ReviewList>
                  {reviews.map((review) => (
                    <ReviewCard key={review._id}>
                      <ReviewHead>
                        <Reviewer>{review.name}</Reviewer>
                        <DateText>{new Date(review.createdAt).toLocaleDateString()}</DateText>
                      </ReviewHead>
                      <Stars>{getStarString(review.rating)}</Stars>
                      <ReviewContent>{review.content}</ReviewContent>
                    </ReviewCard>
                  ))}
                </ReviewList>
              )}
            </ReviewPanel>
          </Grid>
        </Content>
      </Page>
    </>
  );
};

export default Reviews;
